// client/src/utils/useInventoryModal.js
import { useCallback } from "react";

export default function useInventoryModal({
  gridApi,
  pageRows,
  selectedItem,
  setSelectedItem,
  setSelectedImage,
  setModalTitle,
  setModalPrice,
  setModalQty,
  toNumberOrNull,
  toIntOr,
  commitAll,
}) {
  // ------------------------------
  // Commit wrapper
  // ------------------------------
  const runCommit = useCallback(async () => {
    if (typeof commitAll === "function") {
      await commitAll();
    }
  }, [commitAll]);

  // ------------------------------
  // gotoIndex: jump to specific row
  // ------------------------------
  const gotoIndex = useCallback(
    async (idx) => {
      if (!Array.isArray(pageRows)) return;
      if (idx < 0 || idx >= pageRows.length) return;

      // Ensure edits are saved before switching
      await runCommit();

      const row = pageRows[idx];
      if (!row) return;

      // Update modal state
      setSelectedItem(row);
      setSelectedImage(row.url || "");
      setModalTitle(String(row?.title ?? ""));

      const priceVal = toNumberOrNull(row?.per_item_cost_estimate);
      setModalPrice(priceVal === null ? "" : String(priceVal));

      const qtyVal = toIntOr(row?.quantity_estimate, 1);
      setModalQty(String(qtyVal));

      // Sync AG Grid selection
      if (gridApi && typeof gridApi.getDisplayedRowAtIndex === "function") {
        const node = gridApi.getDisplayedRowAtIndex(idx);
        if (node) {
          gridApi.ensureIndexVisible(node.rowIndex, "middle");
          gridApi.deselectAll();
          node.setSelected(true);
        }
      }
    },
    [
      pageRows,
      runCommit,
      setSelectedItem,
      setSelectedImage,
      setModalTitle,
      setModalPrice,
      setModalQty,
      toNumberOrNull,
      toIntOr,
      gridApi,
    ]
  );

  // ------------------------------
  // gotoRel: move by +/- 1 item
  // ------------------------------
  const gotoRel = useCallback(
    async (delta) => {
      if (!selectedItem || !Array.isArray(pageRows)) return;

      const currentIndex = pageRows.findIndex((r) => r.id === selectedItem.id);
      const base = currentIndex === -1 ? 0 : currentIndex;

      await gotoIndex(base + delta);
    },
    [selectedItem, pageRows, gotoIndex]
  );

  return {
    gotoIndex,
    gotoRel,
    commitAll, // Pass-through
  };
}
